import { Badge } from '@art-of-hospitality-solutions-llc/ui-kit/components/badge';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { Checkbox } from '@art-of-hospitality-solutions-llc/ui-kit/components/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/popover';
import { ChevronsUpDown, X } from 'lucide-react';

import FieldShell from './FieldShell';

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectFieldProps {
  title?: string;
  description?: string;
  placeholder?: string;
  options: MultiSelectOption[];
  /** Selected option values. */
  values: string[];
  onChange: (values: string[]) => void;
}

/**
 * Checkbox-list popover with the selection shown as removable chips —
 * "any of these" pickers (venues today, tags/categories next).
 */
export default function MultiSelectField({
  title,
  description,
  placeholder = 'Select...',
  options,
  values,
  onChange,
}: MultiSelectFieldProps) {
  const toggle = (value: string) =>
    onChange(
      values.includes(value)
        ? values.filter((v) => v !== value)
        : [...values, value],
    );

  const labelFor = (value: string) =>
    options.find((o) => o.value === value)?.label ?? value;

  return (
    <FieldShell title={title} description={description}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="w-full justify-between font-normal text-zinc-600"
          >
            {values.length === 0 ? placeholder : `${values.length} selected`}
            <ChevronsUpDown className="size-4 text-zinc-400" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="max-h-64 w-64 overflow-y-auto p-1"
        >
          {options.length === 0 && (
            <div className="p-2 text-sm text-zinc-500">No options</div>
          )}
          {options.map((option) => (
            <label
              key={option.value}
              className="flex cursor-pointer items-center gap-2 rounded-md p-2 text-sm hover:bg-zinc-100"
            >
              <Checkbox
                checked={values.includes(option.value)}
                onCheckedChange={() => toggle(option.value)}
              />
              <span className="min-w-0 truncate">{option.label}</span>
            </label>
          ))}
        </PopoverContent>
      </Popover>

      {values.length > 0 && (
        <div className="flex flex-wrap gap-1 pt-1.5">
          {values.map((value) => (
            <Badge
              key={value}
              variant="secondary"
              className="gap-1 pr-1 font-normal"
            >
              {labelFor(value)}
              <button
                type="button"
                aria-label={`Remove ${labelFor(value)}`}
                onClick={() => toggle(value)}
                className="rounded-full p-0.5 hover:bg-zinc-300/60"
              >
                <X className="size-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </FieldShell>
  );
}
